const BASE = '';  // CRA proxy forwards /admin/* and /* to localhost:8080

async function request(method, path, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',   // send refresh cookie
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  let data;
  try { data = await res.json(); } catch { data = {}; }

  if (!res.ok) {
    throw new Error(data.message || `Request failed: ${res.status}`);
  }
  return data;
}

export const api = {
  get:    (path, token)         => request('GET',    path, null, token),
  post:   (path, body, token)   => request('POST',   path, body, token),
  patch:  (path, body, token)   => request('PATCH',  path, body, token),
  delete: (path, token)         => request('DELETE', path, null, token),
};

// ── Flashcard helpers ─────────────────────────────────────────────────────

export const flashcardApi = {
  list:   (token)       => api.get('/api/admin/flashcards', token),
  get:    (id, token)   => api.get(`/api/admin/flashcards/${id}`, token),
  create: (fc, token)   => api.post('/api/admin/flashcards', fc, token),
  update: (id, fc, token) => api.patch(`/api/admin/flashcards/${id}`, fc, token),
  delete: (id, token)   => api.delete(`/api/admin/flashcards/${id}`, token),
};

// ── User / Preferences helpers ────────────────────────────────────────────

export const userApi = {
  me:               (token)          => api.get('/api/admin/me', token),
  updateMe:         (data, token)    => api.patch('/api/admin/me', data, token),
  getPreferences:   (token)          => api.get('/api/admin/preferences', token),
  savePreferences:  (prefs, token)   => api.patch('/api/admin/preferences', prefs, token),
};

// ── Marketplace helpers ───────────────────────────────────────────────────

export const marketplaceApi = {
  list:        (token)                      => api.get('/api/admin/marketplace', token),
  myPurchases: (token)                      => api.get('/api/admin/marketplace/purchases', token),
  purchase:    (mpoId, token)               => api.post('/api/admin/marketplace/purchase', { mpo_id: mpoId }, token),
  submit:      (playlistId, price, token)   => api.post('/api/admin/marketplace/submit', { playlist_id: playlistId, price }, token),
};

// ── MarketplaceReview helpers ─────────────────────────────────────────────────

export const reviewApi = {
  create:      (mpoId, rating, reviewText, token) =>
    api.post('/api/admin/marketplace/reviews', { mpo_id: mpoId, mpo_rating: rating, mpo_review: reviewText }, token),
  listByMpo:   (mpoId, token)  => api.get(`/api/admin/marketplace/${mpoId}/reviews`, token),
  getMyReviews: (mpoId, token) => api.get(`/api/admin/marketplace/${mpoId}/my-reviews`, token),
  update:      (id, rating, reviewText, token) =>
    api.patch(`/api/admin/marketplace/reviews/${id}`, { mpo_rating: rating, mpo_review: reviewText }, token),
  delete:      (id, token)     => api.delete(`/api/admin/marketplace/reviews/${id}`, token),
};

// ── Tag helpers ───────────────────────────────────────────────────────────

export const tagApi = {
  // User tag library
  list:           (token)                => api.get('/api/admin/tags', token),
  create:         (name, token)          => api.post('/api/admin/tags', { tag: name }, token),
  update:         (id, name, token)      => api.patch(`/api/admin/tags/${id}`, { tag: name }, token),
  delete:         (id, token)            => api.delete(`/api/admin/tags/${id}`, token),
  // Flashcard-tag associations
  listForCard:    (cardId, token)        => api.get(`/api/admin/flashcards/${cardId}/tags`, token),
  addToCard:      (cardId, tagId, token) => api.post(`/api/admin/flashcards/${cardId}/tags/${tagId}`, {}, token),
  removeFromCard: (cardId, tagId, token) => api.delete(`/api/admin/flashcards/${cardId}/tags/${tagId}`, token),
  // Bulk load for quiz mode: returns [{flashcard_id, tag_id, tag}]
  listAllForUser: (token)                => api.get('/api/admin/flashcard-tags', token),
};

// ── Playlist helpers ──────────────────────────────────────────────────────

export const playlistApi = {
  list:      (token)              => api.get('/api/admin/playlists', token),
  get:       (id, token)          => api.get(`/api/admin/playlists/${id}`, token),
  create:    (data, token)        => api.post('/api/admin/playlists', data, token),
  update:    (id, data, token)    => api.patch(`/api/admin/playlists/${id}`, data, token),
  delete:    (id, token)          => api.delete(`/api/admin/playlists/${id}`, token),
  getPublic: (shareToken)         => api.get(`/api/playlists/public/${shareToken}`),
};

// ── Quiz helpers ──────────────────────────────────────────────────────────

export const quizApi = {
  report:           (ids, token)         => api.post('/api/admin/quiz/report', { ids }, token),
  createPublic:     (title, ids, token)  => api.post('/api/admin/quiz/public', { title, flashcard_ids: ids }, token),
  listPublic:       (token)              => api.get('/api/admin/quiz/public', token),
  getPublic:        (shareToken)         => api.get(`/api/quiz/public/${shareToken}`),
};
